/**
 * Draft model for the visual mapper builder.
 *
 * The stored mapper config is the single source of truth, but a few of its shapes
 * (`input.topicCaptures`, `lookups`, `ddr.routing`) are objects, and objects cannot be
 * edited key-by-key in a form without losing row order and input focus. `draftFromConfig`
 * turns those into keyed row arrays, `configFromDraft` turns them back.
 *
 * Anything the draft model cannot express (a future DSL version, an unknown value-spec
 * kind, an unknown transform) makes `draftFromConfig` return `null` so the editor can fall
 * back to read-write code mode instead of silently dropping keys.
 */
import cloneDeep from 'lodash/cloneDeep';
import isEqual from 'lodash/isEqual';

export const OUTPUT_SHAPES = ['direct', 'ddr'];
export const VALUE_SPEC_KINDS = ['literal', 'path', 'capture', 'lookup'];
export const LITERAL_TYPES = ['text', 'number', 'boolean', 'null', 'json'];
export const LOOKUP_KEY_SOURCES = ['from', 'key', 'none'];
export const TRANSFORM_TYPES = [
  'number',
  'string',
  'boolean',
  'timestampIso',
  'multiply',
  'divide',
  'scalePow10',
  'default',
];

const ARGUMENT_FREE_TRANSFORMS = ['number', 'string', 'boolean', 'timestampIso'];

let uiKeyCounter = 0;

function nextUiKey(prefix) {
  uiKeyCounter += 1;
  return `${prefix}-${uiKeyCounter}`;
}

function isRecord(value) {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

class UnrepresentableConfigError extends Error {
  constructor(detail) {
    super(`Mapper config cannot be represented in the builder: ${detail}`);
    this.name = 'UnrepresentableConfigError';
  }
}

function unrepresentable(detail) {
  throw new UnrepresentableConfigError(detail);
}

export function defaultMapperConfig() {
  return { version: 1, output: 'direct', emit: [] };
}

// --- literal values -------------------------------------------------------

export function literalDraftFromValue(value) {
  if (typeof value === 'string') {
    return { type: 'text', text: value };
  }
  if (typeof value === 'number') {
    return { type: 'number', text: String(value) };
  }
  if (typeof value === 'boolean') {
    return { type: 'boolean', text: String(value) };
  }
  if (value === null) {
    return { type: 'null', text: '' };
  }
  return { type: 'json', text: JSON.stringify(value) };
}

export function literalValueFromDraft(draft) {
  const text = draft && typeof draft.text === 'string' ? draft.text : '';
  switch (draft && draft.type) {
    case 'number': {
      const parsed = Number(text);
      return text.trim() === '' || Number.isNaN(parsed) ? text : parsed;
    }
    case 'boolean':
      return text === 'true';
    case 'null':
      return null;
    case 'json':
      try {
        return JSON.parse(text);
      } catch (_) {
        return text;
      }
    default:
      return text;
  }
}

export function emptyLiteralDraft(type) {
  return { type: type || 'text', text: type === 'boolean' ? 'false' : '' };
}

// --- transforms -----------------------------------------------------------

export function emptyTransformDraft(type) {
  return {
    uiKey: nextUiKey('transform'),
    type: type || 'number',
    amount: type === 'divide' || type === 'multiply' ? '1' : '0',
    scaleSource: 'number',
    spec: null,
    literal: emptyLiteralDraft('text'),
  };
}

function transformDraftFromConfig(transform) {
  const draft = emptyTransformDraft('number');

  if (typeof transform === 'string') {
    if (!ARGUMENT_FREE_TRANSFORMS.includes(transform)) {
      unrepresentable(`unknown transform '${transform}'`);
    }
    draft.type = transform;
    return draft;
  }

  if (!isRecord(transform)) {
    unrepresentable('transform must be a string or an object');
  }

  const keys = Object.keys(transform);
  if (keys.length !== 1) {
    unrepresentable('transform must hold exactly one key');
  }

  const [type] = keys;
  const argument = transform[type];
  draft.type = type;

  if (type === 'multiply' || type === 'divide') {
    if (typeof argument !== 'number') {
      unrepresentable(`${type} expects a number`);
    }
    draft.amount = String(argument);
    return draft;
  }
  if (type === 'scalePow10') {
    if (typeof argument === 'number') {
      draft.scaleSource = 'number';
      draft.amount = String(argument);
    } else {
      draft.scaleSource = 'value';
      draft.spec = valueSpecDraftFromConfig(argument);
    }
    return draft;
  }
  if (type === 'default') {
    draft.literal = literalDraftFromValue(argument);
    return draft;
  }

  return unrepresentable(`unknown transform '${type}'`);
}

function transformConfigFromDraft(draft) {
  if (ARGUMENT_FREE_TRANSFORMS.includes(draft.type)) {
    return draft.type;
  }
  if (draft.type === 'multiply' || draft.type === 'divide') {
    return { [draft.type]: numericValueFromDraft(draft.amount) };
  }
  if (draft.type === 'scalePow10') {
    return {
      scalePow10:
        draft.scaleSource === 'value' ? valueSpecConfigFromDraft(draft.spec) : numericValueFromDraft(draft.amount),
    };
  }
  return { default: literalValueFromDraft(draft.literal) };
}

function numericValueFromDraft(amount) {
  const number = Number(amount);
  return typeof amount === 'string' && (amount.trim() === '' || !Number.isFinite(number)) ? amount : number;
}

// --- value specs ----------------------------------------------------------

export function emptyValueSpecDraft(kind) {
  return {
    uiKey: nextUiKey('value'),
    kind: kind || 'literal',
    literal: emptyLiteralDraft('text'),
    path: '',
    capture: '',
    lookup: {
      table: '',
      source: 'from',
      from: null,
      key: emptyLiteralDraft('text'),
    },
    transforms: [],
  };
}

function applyLookupToDraft(draft, lookup) {
  if (!isRecord(lookup) || typeof lookup.table !== 'string') {
    unrepresentable('lookup needs a table name');
  }
  const lookupKeys = Object.keys(lookup).filter((key) => key !== 'table');
  if (lookupKeys.some((key) => key !== 'from' && key !== 'key')) {
    unrepresentable('lookup holds unknown fields');
  }
  if (lookupKeys.length > 1) {
    unrepresentable('lookup uses both from and key');
  }

  draft.kind = 'lookup';
  draft.lookup.table = lookup.table;
  if ('from' in lookup) {
    draft.lookup.source = 'from';
    draft.lookup.from = valueSpecDraftFromConfig(lookup.from);
  } else if ('key' in lookup) {
    draft.lookup.source = 'key';
    draft.lookup.key = literalDraftFromValue(lookup.key);
  } else {
    draft.lookup.source = 'none';
  }
}

function applyKindToDraft(draft, value) {
  if ('path' in value) {
    if (typeof value.path !== 'string') {
      unrepresentable('path must be a string');
    }
    draft.kind = 'path';
    draft.path = value.path;
    return;
  }
  if ('capture' in value) {
    if (typeof value.capture !== 'string') {
      unrepresentable('capture must be a string');
    }
    draft.kind = 'capture';
    draft.capture = value.capture;
    return;
  }
  if ('lookup' in value) {
    applyLookupToDraft(draft, value.lookup);
    return;
  }
  unrepresentable(`unknown value spec with fields ${Object.keys(value).join(', ')}`);
}

export function valueSpecDraftFromConfig(value) {
  if (value === undefined) {
    return null;
  }

  const draft = emptyValueSpecDraft('literal');

  if (value === null || typeof value !== 'object') {
    draft.kind = 'literal';
    draft.literal = literalDraftFromValue(value);
    return draft;
  }

  if (Array.isArray(value)) {
    unrepresentable('value specs cannot be arrays');
  }

  const keys = Object.keys(value);
  if (value.transforms !== undefined) {
    if (!Array.isArray(value.transforms)) {
      unrepresentable('transforms must be an array');
    }
    draft.transforms = value.transforms.map(transformDraftFromConfig);
  }

  applyKindToDraft(draft, value);

  const allowed = draft.kind === 'literal' ? [] : [draft.kind, 'transforms'];
  const unknown = keys.filter((key) => !allowed.includes(key));
  if (unknown.length > 0) {
    unrepresentable(`unknown value spec fields ${unknown.join(', ')}`);
  }

  return draft;
}

export function valueSpecConfigFromDraft(draft) {
  if (!draft) {
    return undefined;
  }

  const transforms = (draft.transforms || []).map(transformConfigFromDraft);

  if (draft.kind === 'literal') {
    // Literals carry no transforms in the DSL, they are already the final value.
    return literalValueFromDraft(draft.literal);
  }

  let spec;
  if (draft.kind === 'path') {
    spec = { path: draft.path || '' };
  } else if (draft.kind === 'capture') {
    spec = { capture: draft.capture || '' };
  } else {
    const lookup = { table: draft.lookup.table || '' };
    if (draft.lookup.source === 'from') {
      lookup.from = draft.lookup.from ? valueSpecConfigFromDraft(draft.lookup.from) : { path: '' };
    } else if (draft.lookup.source === 'key') {
      lookup.key = literalValueFromDraft(draft.lookup.key);
    }
    spec = { lookup };
  }

  if (transforms.length > 0) {
    spec.transforms = transforms;
  }
  return spec;
}

/** Compact, language-independent description of a value spec for its chip. */
export function valueSpecSummary(draft) {
  if (!draft) {
    return { kind: 'unset', text: '', transformCount: 0 };
  }

  const transformCount = (draft.transforms || []).length;

  if (draft.kind === 'literal') {
    // Literals are already their final value, the DSL has no transforms for them.
    const value = literalValueFromDraft(draft.literal);
    return {
      kind: 'literal',
      text: typeof value === 'string' ? `"${value}"` : JSON.stringify(value),
      transformCount: 0,
    };
  }
  if (draft.kind === 'path') {
    return { kind: 'path', text: draft.path || '…', transformCount };
  }
  if (draft.kind === 'capture') {
    return { kind: 'capture', text: draft.capture || '…', transformCount };
  }

  const table = draft.lookup.table || '…';
  if (draft.lookup.source === 'from') {
    const from = valueSpecSummary(draft.lookup.from);
    return { kind: 'lookup', text: `${table}[${from.text || '…'}]`, transformCount };
  }
  if (draft.lookup.source === 'key') {
    const key = literalValueFromDraft(draft.lookup.key);
    return { kind: 'lookup', text: `${table}[${JSON.stringify(key)}]`, transformCount };
  }
  return { kind: 'lookup', text: `${table}[]`, transformCount };
}

// --- emit rules -----------------------------------------------------------

export function emptyUpdateDraft() {
  return {
    uiKey: nextUiKey('update'),
    stateId: emptyValueSpecDraft('literal'),
    value: emptyValueSpecDraft('path'),
    time: null,
    valueType: null,
  };
}

export function emptyDirectDraft() {
  return {
    deviceId: null,
    dtwinId: emptyValueSpecDraft('path'),
    updates: [emptyUpdateDraft()],
  };
}

export function emptyRoutingRowDraft() {
  return { uiKey: nextUiKey('routing'), name: '', spec: emptyValueSpecDraft('path') };
}

export function emptyDdrDraft() {
  return {
    deviceId: null,
    routingSource: 'fields',
    routingRows: [emptyRoutingRowDraft()],
    routingSpec: emptyValueSpecDraft('path'),
    variable: emptyValueSpecDraft('literal'),
    data: {
      value: emptyValueSpecDraft('path'),
      time: null,
      valueType: null,
    },
  };
}

export function emptyEmitRuleDraft(shape) {
  return {
    uiKey: nextUiKey('emit'),
    foreach: '',
    direct: shape === 'ddr' ? null : emptyDirectDraft(),
    ddr: shape === 'ddr' ? emptyDdrDraft() : null,
  };
}

function directDraftFromConfig(direct) {
  if (!isRecord(direct)) {
    unrepresentable('direct output must be an object');
  }
  const unknown = Object.keys(direct).filter((key) => !['deviceId', 'dtwinId', 'updates'].includes(key));
  if (unknown.length > 0) {
    unrepresentable(`unknown direct output fields ${unknown.join(', ')}`);
  }
  if (direct.updates !== undefined && !Array.isArray(direct.updates)) {
    unrepresentable('direct updates must be an array');
  }

  return {
    deviceId: valueSpecDraftFromConfig(direct.deviceId),
    dtwinId: valueSpecDraftFromConfig(direct.dtwinId),
    updates: (direct.updates || []).map((update) => {
      if (!isRecord(update)) {
        unrepresentable('direct update must be an object');
      }
      const unknownUpdate = Object.keys(update).filter(
        (key) => !['stateId', 'value', 'time', 'valueType'].includes(key)
      );
      if (unknownUpdate.length > 0) {
        unrepresentable(`unknown direct update fields ${unknownUpdate.join(', ')}`);
      }
      return {
        uiKey: nextUiKey('update'),
        stateId: valueSpecDraftFromConfig(update.stateId),
        value: valueSpecDraftFromConfig(update.value),
        time: valueSpecDraftFromConfig(update.time),
        valueType: valueSpecDraftFromConfig(update.valueType),
      };
    }),
  };
}

function directConfigFromDraft(draft) {
  const direct = {};
  if (draft.deviceId) {
    direct.deviceId = valueSpecConfigFromDraft(draft.deviceId);
  }
  if (draft.dtwinId) {
    direct.dtwinId = valueSpecConfigFromDraft(draft.dtwinId);
  }
  direct.updates = draft.updates.map((update) => {
    const row = {};
    if (update.stateId) {
      row.stateId = valueSpecConfigFromDraft(update.stateId);
    }
    if (update.value) {
      row.value = valueSpecConfigFromDraft(update.value);
    }
    if (update.time) {
      row.time = valueSpecConfigFromDraft(update.time);
    }
    if (update.valueType) {
      row.valueType = valueSpecConfigFromDraft(update.valueType);
    }
    return row;
  });
  return direct;
}

function isRoutingFieldMap(routing) {
  // `routing` is either a flat object of DDR selector fields or a single value spec.
  if (!isRecord(routing)) {
    return false;
  }
  return !['path', 'capture', 'lookup'].some((key) => key in routing);
}

function ddrDraftFromConfig(ddr) {
  if (!isRecord(ddr)) {
    unrepresentable('ddr output must be an object');
  }
  const unknown = Object.keys(ddr).filter((key) => !['deviceId', 'routing', 'variable', 'data'].includes(key));
  if (unknown.length > 0) {
    unrepresentable(`unknown ddr output fields ${unknown.join(', ')}`);
  }
  const data = ddr.data === undefined ? {} : ddr.data;
  if (!isRecord(data)) {
    unrepresentable('ddr data must be an object');
  }
  const unknownData = Object.keys(data).filter((key) => !['value', 'time', 'valueType'].includes(key));
  if (unknownData.length > 0) {
    unrepresentable(`unknown ddr data fields ${unknownData.join(', ')}`);
  }

  const draft = emptyDdrDraft();
  draft.deviceId = valueSpecDraftFromConfig(ddr.deviceId);
  draft.variable = valueSpecDraftFromConfig(ddr.variable);
  draft.data = {
    value: valueSpecDraftFromConfig(data.value),
    time: valueSpecDraftFromConfig(data.time),
    valueType: valueSpecDraftFromConfig(data.valueType),
  };

  if (ddr.routing === undefined) {
    draft.routingSource = 'fields';
    draft.routingRows = [];
  } else if (isRoutingFieldMap(ddr.routing)) {
    draft.routingSource = 'fields';
    draft.routingRows = Object.entries(ddr.routing).map(([name, spec]) => ({
      uiKey: nextUiKey('routing'),
      name,
      spec: valueSpecDraftFromConfig(spec),
    }));
  } else {
    draft.routingSource = 'value';
    draft.routingSpec = valueSpecDraftFromConfig(ddr.routing);
  }

  return draft;
}

function ddrConfigFromDraft(draft) {
  const ddr = {};
  if (draft.deviceId) {
    ddr.deviceId = valueSpecConfigFromDraft(draft.deviceId);
  }
  if (draft.routingSource === 'value') {
    ddr.routing = valueSpecConfigFromDraft(draft.routingSpec);
  } else if (draft.routingRows.length > 0) {
    ddr.routing = mapDraftRows(
      draft.routingRows,
      (row) => row.name,
      (row) => valueSpecConfigFromDraft(row.spec)
    );
  }
  if (draft.variable) {
    ddr.variable = valueSpecConfigFromDraft(draft.variable);
  }
  ddr.data = {};
  if (draft.data.value) {
    ddr.data.value = valueSpecConfigFromDraft(draft.data.value);
  }
  if (draft.data.time) {
    ddr.data.time = valueSpecConfigFromDraft(draft.data.time);
  }
  if (draft.data.valueType) {
    ddr.data.valueType = valueSpecConfigFromDraft(draft.data.valueType);
  }
  return ddr;
}

function emitRuleDraftFromConfig(rule) {
  if (!isRecord(rule)) {
    unrepresentable('emit rule must be an object');
  }
  const unknown = Object.keys(rule).filter((key) => !['foreach', 'output'].includes(key));
  if (unknown.length > 0) {
    unrepresentable(`unknown emit rule fields ${unknown.join(', ')}`);
  }
  if (rule.foreach !== undefined && typeof rule.foreach !== 'string') {
    unrepresentable('foreach must be a string');
  }
  const output = rule.output === undefined ? {} : rule.output;
  if (!isRecord(output)) {
    unrepresentable('emit output must be an object');
  }
  const unknownOutput = Object.keys(output).filter((key) => !['direct', 'ddr'].includes(key));
  if (unknownOutput.length > 0) {
    unrepresentable(`unknown emit output fields ${unknownOutput.join(', ')}`);
  }

  return {
    uiKey: nextUiKey('emit'),
    foreach: rule.foreach || '',
    direct: 'direct' in output ? directDraftFromConfig(output.direct) : null,
    ddr: 'ddr' in output ? ddrDraftFromConfig(output.ddr) : null,
  };
}

function emitRuleConfigFromDraft(draft) {
  const rule = {};
  if (draft.foreach) {
    rule.foreach = draft.foreach;
  }
  rule.output = {};
  if (draft.direct) {
    rule.output.direct = directConfigFromDraft(draft.direct);
  }
  if (draft.ddr) {
    rule.output.ddr = ddrConfigFromDraft(draft.ddr);
  }
  return rule;
}

// --- lookups and input ----------------------------------------------------

export function emptyLookupRowDraft() {
  return { uiKey: nextUiKey('lookup-row'), key: '', value: emptyLiteralDraft('text') };
}

export function emptyLookupTableDraft() {
  return { uiKey: nextUiKey('lookup'), name: '', rows: [emptyLookupRowDraft()] };
}

export function emptyCaptureDraft() {
  return { uiKey: nextUiKey('capture'), name: '', index: 1 };
}

function mapDraftRows(rows, keyForRow, valueForRow) {
  return rows.reduce((result, row) => {
    Object.defineProperty(result, keyForRow(row), {
      configurable: true,
      enumerable: true,
      value: valueForRow(row),
      writable: true,
    });
    return result;
  }, Object.create(null));
}

function duplicateNamedRows(rows, keyForRow, label) {
  const names = new Set();
  return rows.reduce((errors, row) => {
    const name = keyForRow(row);
    if (names.has(name)) {
      errors.push(`duplicate ${label}`);
    }
    names.add(name);
    return errors;
  }, []);
}

function invalidValueSpecNumbers(spec) {
  if (!spec) {
    return false;
  }
  const hasInvalidTransform = (spec.transforms || []).some((transform) => {
    if (transform.type === 'multiply' || transform.type === 'divide') {
      return !Number.isFinite(Number(transform.amount)) || String(transform.amount).trim() === '';
    }
    if (transform.type === 'scalePow10') {
      return (
        (transform.scaleSource !== 'value' &&
          (!Number.isFinite(Number(transform.amount)) || String(transform.amount).trim() === '')) ||
        (transform.scaleSource === 'value' && invalidValueSpecNumbers(transform.spec))
      );
    }
    return false;
  });
  return hasInvalidTransform || (spec.lookup && invalidValueSpecNumbers(spec.lookup.from));
}

/** Errors that would make the form lose a keyed row or emit an invalid number. */
export function draftValidationErrors(draft) {
  if (!draft) {
    return [];
  }
  const errors = [
    ...duplicateNamedRows(draft.input.captures, (capture) => capture.name, 'capture name'),
    ...duplicateNamedRows(draft.lookups, (table) => table.name, 'lookup table name'),
  ];

  draft.input.captures.forEach((capture) => {
    if (!Number.isInteger(capture.index) || capture.index < 0) {
      errors.push('invalid capture group');
    }
  });
  draft.lookups.forEach((table) => {
    errors.push(...duplicateNamedRows(table.rows, (row) => row.key, 'lookup key'));
  });
  draft.emit.forEach((rule) => {
    const ddr = rule.ddr;
    if (ddr && ddr.routingSource === 'fields') {
      errors.push(...duplicateNamedRows(ddr.routingRows, (row) => row.name, 'routing field'));
    }
  });

  return errors.concat(containsInvalidTransform(draft) ? ['invalid transform amount'] : []);
}

function containsInvalidTransform(draft) {
  const inspectValue = (spec) => invalidValueSpecNumbers(spec);
  return draft.emit.some((rule) => {
    const output = rule.direct || rule.ddr;
    if (!output) {
      return false;
    }
    if (rule.direct) {
      return [output.deviceId, output.dtwinId, ...output.updates.flatMap((update) => Object.values(update))].some(
        inspectValue
      );
    }
    return [
      output.deviceId,
      output.routingSpec,
      output.variable,
      output.data.value,
      output.data.time,
      output.data.valueType,
      ...output.routingRows.map((row) => row.spec),
    ].some(inspectValue);
  });
}

// --- config <-> draft -----------------------------------------------------

function isEmptyConfig(config) {
  return config === undefined || config === null || (isRecord(config) && Object.keys(config).length === 0);
}

/** Returns a draft for the builder form, or `null` when the config is not representable. */
export function draftFromConfig(config) {
  const source = isEmptyConfig(config) ? defaultMapperConfig() : config;

  try {
    if (!isRecord(source)) {
      unrepresentable('config must be an object');
    }
    const unknown = Object.keys(source).filter(
      (key) => !['version', 'output', 'input', 'lookups', 'emit'].includes(key)
    );
    if (unknown.length > 0) {
      unrepresentable(`unknown config fields ${unknown.join(', ')}`);
    }
    if (source.version !== 1) {
      unrepresentable(`unsupported DSL version ${JSON.stringify(source.version)}`);
    }
    if (!OUTPUT_SHAPES.includes(source.output)) {
      unrepresentable(`unsupported output shape ${JSON.stringify(source.output)}`);
    }
    if (source.emit !== undefined && !Array.isArray(source.emit)) {
      unrepresentable('emit must be an array');
    }

    const input = source.input === undefined ? {} : source.input;
    if (!isRecord(input)) {
      unrepresentable('input must be an object');
    }
    const unknownInput = Object.keys(input).filter((key) => !['root', 'topicPattern', 'topicCaptures'].includes(key));
    if (unknownInput.length > 0) {
      unrepresentable(`unknown input fields ${unknownInput.join(', ')}`);
    }
    const topicCaptures = input.topicCaptures === undefined ? {} : input.topicCaptures;
    if (!isRecord(topicCaptures)) {
      unrepresentable('topicCaptures must be an object');
    }

    const lookups = source.lookups === undefined ? {} : source.lookups;
    if (!isRecord(lookups)) {
      unrepresentable('lookups must be an object');
    }

    return {
      output: source.output,
      input: {
        root: typeof input.root === 'string' ? input.root : '',
        topicPattern: typeof input.topicPattern === 'string' ? input.topicPattern : '',
        captures: Object.entries(topicCaptures).map(([name, index]) => ({
          uiKey: nextUiKey('capture'),
          name,
          index,
        })),
      },
      lookups: Object.entries(lookups).map(([name, table]) => {
        if (!isRecord(table)) {
          unrepresentable(`lookup table '${name}' must be an object`);
        }
        return {
          uiKey: nextUiKey('lookup'),
          name,
          rows: Object.entries(table).map(([key, value]) => ({
            uiKey: nextUiKey('lookup-row'),
            key,
            value: literalDraftFromValue(value),
          })),
        };
      }),
      emit: (source.emit || []).map(emitRuleDraftFromConfig),
    };
  } catch (error) {
    if (error instanceof UnrepresentableConfigError) {
      return null;
    }
    throw error;
  }
}

export function configFromDraft(draft) {
  const config = { version: 1, output: draft.output };

  const input = {};
  if (draft.input.root) {
    input.root = draft.input.root;
  }
  if (draft.input.topicPattern) {
    input.topicPattern = draft.input.topicPattern;
  }
  if (draft.input.captures.length > 0) {
    input.topicCaptures = mapDraftRows(
      draft.input.captures,
      (capture) => capture.name,
      (capture) => capture.index
    );
  }
  if (Object.keys(input).length > 0) {
    config.input = input;
  }

  if (draft.lookups.length > 0) {
    config.lookups = mapDraftRows(
      draft.lookups,
      (table) => table.name,
      (table) =>
        mapDraftRows(
          table.rows,
          (row) => row.key,
          (row) => literalValueFromDraft(row.value)
        )
    );
  }

  config.emit = draft.emit.map(emitRuleConfigFromDraft);

  return config;
}

function stripEmptyTransforms(node) {
  if (Array.isArray(node)) {
    node.forEach(stripEmptyTransforms);
    return;
  }
  if (!isRecord(node)) {
    return;
  }
  if (Array.isArray(node.transforms) && node.transforms.length === 0) {
    delete node.transforms;
  }
  Object.values(node).forEach(stripEmptyTransforms);
}

/**
 * Drops the differences that carry no meaning in the DSL (empty optional strings, empty
 * optional objects, empty transform lists) so builder output can be compared against a
 * hand-written config without reporting a difference the user cannot see.
 */
export function canonicalMapperConfig(config) {
  if (!isRecord(config)) {
    return config;
  }
  const clone = cloneDeep(config);

  if (isRecord(clone.input)) {
    if (clone.input.root === '') {
      delete clone.input.root;
    }
    if (clone.input.topicPattern === '') {
      delete clone.input.topicPattern;
    }
    if (isRecord(clone.input.topicCaptures) && Object.keys(clone.input.topicCaptures).length === 0) {
      delete clone.input.topicCaptures;
    }
    if (Object.keys(clone.input).length === 0) {
      delete clone.input;
    }
  }
  if (isRecord(clone.lookups) && Object.keys(clone.lookups).length === 0) {
    delete clone.lookups;
  }
  if (Array.isArray(clone.emit)) {
    clone.emit.forEach((rule) => {
      if (isRecord(rule) && rule.foreach === '') {
        delete rule.foreach;
      }
      const ddr = rule && rule.output && rule.output.ddr;
      if (isRecord(ddr) && isRecord(ddr.routing) && Object.keys(ddr.routing).length === 0) {
        delete ddr.routing;
      }
    });
  }
  stripEmptyTransforms(clone);

  return clone;
}

/** True when the builder can show the config and hand it back unchanged. */
export function isRepresentableConfig(config) {
  if (isEmptyConfig(config)) {
    return true;
  }
  const draft = draftFromConfig(config);
  if (!draft) {
    return false;
  }
  return isEqual(canonicalMapperConfig(configFromDraft(draft)), canonicalMapperConfig(config));
}
